import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Template } from "src/components/pages/templates/templateDisplayCard";
import {
  completedInspectionStatus,
  inProgressInspectionStatus,
} from "src/constants/constants";
import { initialResult } from "src/data/mockData";
import {
  AnalyzedResult,
  Inspection,
  InspectionDetail,
  InspectionStatus,
  InspectionTemplate,
  Question,
  Result,
} from "src/data/types";
import { apiRequest } from "src/utils/apiRequest";
type UpdateResultPayload = {
  inspection_id: string;
  question_id: string;
  formData: FormData;
};
export type CreateInspectionPayload = {
  templateId: string;
  templateName: string;
  siteCode: string;
};
export type UpdateInspectionPayload = {
  inspectionId: string;
  templateId: string;
  templateName: string;
  siteCode: string;
  status: InspectionStatus;
  createdBy: string;
  createdAt: string;
};
export type DataState = {
  inspectionTemplates: Template[];
  selectedInspectionTemplate: InspectionTemplate | null;
  activeQuestion: Question | null;
  capturedImageSrc: string;
  imageCapturingMode: boolean;
  userInspections: Inspection[];
  incompleteInspections: Inspection[];
  completedInspections: Inspection[];
  currentCreatedInspection: Inspection;
  readyToSubmitReport: boolean;
  inspectionResults: Result[];
  inspectionResult: Result;
  doneInspectingCurrentQuestion: boolean;
  currentAnalyzedResult: AnalyzedResult;
  selectedInspectionDetail: InspectionDetail;
  evidenceSample: File | string;
};

const initialState: DataState = {
  inspectionTemplates: [],
  selectedInspectionTemplate: null,
  activeQuestion: null,
  capturedImageSrc: "",
  imageCapturingMode: true,
  userInspections: [],
  incompleteInspections: [],
  completedInspections: [],
  currentCreatedInspection: {
    complianceStats: {
      compliantCount: 0,
      questionCount: 0,
    },
    createdAt: "",
    inspectionId: "",
    siteCode: "",
    templateId: "",
    templateName: "",
    status: inProgressInspectionStatus,
    createdBy: "",
  },
  readyToSubmitReport: false,
  inspectionResults: [],
  inspectionResult: initialResult,
  doneInspectingCurrentQuestion: false,
  currentAnalyzedResult: {
    aiReasoning: "",
    aiResult: "",
    analysisTimestamp: "",
  },
  selectedInspectionDetail: {
    completedAt: null,
    createdAt: "",
    createdBy: "",
    inspectionId: "",
    results: [],
    siteCode: "",
    status: inProgressInspectionStatus,
    templateId: "",
    templateName: "",
  },
  evidenceSample: "",
};
export const getAllInspectionTemplates = createAsyncThunk(
  "data/getAllInspectionTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiRequest({ url: "/templates", method: "GET" });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch template");
    }
  }
);
export const getTemplateById = createAsyncThunk(
  "data/getTemplateById",
  async (templateId: string, { rejectWithValue }) => {
    try {
      const data = await apiRequest({
        url: `/templates/${templateId}`,
        method: "GET",
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch template");
    }
  }
);
export const createNewInspection = createAsyncThunk(
  "data/createNewInspection",
  async (
    createInspectionPayload: CreateInspectionPayload,
    { rejectWithValue }
  ) => {
    try {
      const data = await apiRequest({
        url: `/inspections`,
        method: "POST",
        data: createInspectionPayload,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch template");
    }
  }
);
export const updateInspection = createAsyncThunk(
  "data/updateInspection",
  async (
    updateInspectionPayload: UpdateInspectionPayload,
    { rejectWithValue }
  ) => {
    try {
      const data = await apiRequest({
        url: `/inspections`,
        method: "POST",
        data: updateInspectionPayload,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch template");
    }
  }
);
export const getInspectionsByUser = createAsyncThunk(
  "data/getInspectionsByUser",
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiRequest({
        url: `/inspections`,
        method: "GET",
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch inspections");
    }
  }
);
export const getInspectionById = createAsyncThunk(
  "data/getInspectionsById",
  async (inspectionId: string, { rejectWithValue }) => {
    try {
      const data = await apiRequest({
        url: `/inspections/${inspectionId}`,
        method: "GET",
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch template");
    }
  }
);
export const analyzeEvidence = createAsyncThunk(
  "data/analyzeEvidence",
  async (analyzePayload: FormData, { rejectWithValue }) => {
    try {
      const data = await apiRequest({
        url: `/inspections/analyze-evidence`,
        method: "POST",
        data: analyzePayload,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch template");
    }
  }
);
export const updateInspectionResult = createAsyncThunk(
  "data/updateInspectionResult",
  async (updateResultPayload: UpdateResultPayload, { rejectWithValue }) => {
    try {
      const data = await apiRequest({
        url: `/inspections/${updateResultPayload.inspection_id}/questions/${updateResultPayload.question_id}/result`,
        method: "PUT",
        data: updateResultPayload.formData,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch template");
    }
  }
);
/** Data Slice */
const { reducer, actions } = createSlice({
  name: "data",
  initialState,
  reducers: {
    setSelectedInspectionTemplate: (state, { payload }) => {
      state.selectedInspectionTemplate = payload;
    },
    setActiveQuestion: (state, { payload }) => {
      state.activeQuestion = payload;
    },
    setCapturedImageSrc: (state, { payload }) => {
      state.capturedImageSrc = payload;
    },
    setImageCapturingMode: (state, { payload }) => {
      state.imageCapturingMode = payload;
    },
    attachImageToQuestion: (state, { payload }) => {
      if (state.activeQuestion) {
        state.activeQuestion.examples = payload;
      }
    },
    setReadyToSubmitReport: (state, { payload }) => {
      state.readyToSubmitReport = payload;
    },
    setInspectionResults: (state, { payload }) => {
      state.inspectionResults = payload;
    },
    updateInspectionResults: (state, { payload }: { payload: Result }) => {
      const newResultIndex = state.inspectionResults.findIndex(
        (result) => result.questionId === payload.questionId
      );
      if (newResultIndex !== -1) {
        state.inspectionResults[newResultIndex] = payload;
      } else {
        state.inspectionResults.push(payload);
      }
    },
    setInspectionResult: (state, { payload }) => {
      state.inspectionResult = payload;
    },
    setDoneInspectingCurrentQuestion: (state, { payload }) => {
      state.doneInspectingCurrentQuestion = payload;
    },
    resetCurrentAnalyzedResult: (state) => {
      state.currentAnalyzedResult = {
        aiReasoning: "",
        aiResult: "",
        analysisTimestamp: "",
      };
    },
    setSelectedInspectionDetail: (state, { payload }) => {
      state.selectedInspectionDetail = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getAllInspectionTemplates.fulfilled,
      (state, { payload }) => {
        state.inspectionTemplates = payload;
      }
    );
    builder.addCase(getTemplateById.fulfilled, (state, { payload }) => {
      state.selectedInspectionTemplate = payload;
    });
    builder.addCase(
      getInspectionsByUser.fulfilled,
      (state, { payload }: { payload: Inspection[] }) => {
        const incompleteInspections = payload.filter(
          (inspections) => inspections.status === inProgressInspectionStatus
        );
        const completedInspections = payload.filter(
          (inspections) => inspections.status === completedInspectionStatus
        );
        state.incompleteInspections = incompleteInspections;
        state.completedInspections = completedInspections;
        state.userInspections = payload;
      }
    );
    builder.addCase(getInspectionById.fulfilled, (state, { payload }) => {
      state.selectedInspectionDetail = payload;
    });
    builder.addCase(createNewInspection.fulfilled, (state, { payload }) => {
      state.currentCreatedInspection = payload;
    });
    builder.addCase(analyzeEvidence.fulfilled, (state, { payload }) => {
      state.currentAnalyzedResult = payload;
    });
    builder.addCase(updateInspectionResult.fulfilled, (state, { payload }) => {
      // TODO: UPDATE THE STATE HERE
    });
  },
});

export const {
  setSelectedInspectionTemplate,
  setActiveQuestion,
  setCapturedImageSrc,
  setImageCapturingMode,
  attachImageToQuestion,
  setReadyToSubmitReport,
  setInspectionResults,
  updateInspectionResults,
  setInspectionResult,
  setDoneInspectingCurrentQuestion,
  resetCurrentAnalyzedResult,
  setSelectedInspectionDetail,
} = actions;

export default reducer;
